/**
 * Motion for Appearance by Counsel + Order generation (Lloyd, 14 Sep 2026).
 *
 * The same split as the continuance motion, and for the same reason: `readiness` asks what is
 * missing WITHOUT producing anything, so staff see the refusals while filling the form in rather
 * than as a 422 after they press the button. Only `generate` produces documents, and it always
 * produces both — the API has no shape that returns a motion without its order.
 *
 * One refusal here is different from the rest and the panel treats it differently: a case that has
 * appealed out to a court of record can never generate this motion, and no amount of filling fields
 * in changes that.
 */

export interface AbsenceBlockerDto {
  field: string | null;
  reason: string;
}

export type CourtLevelDto = 'JP_OR_MUNICIPAL' | 'COURT_OF_RECORD';

export interface AbsenceReadinessDto {
  canGenerate: boolean;
  blockers: AbsenceBlockerDto[];
  courtLevel: CourtLevelDto;
}

export interface GenerateAbsenceMotionInput {
  settingDate: string;
  settingType?: string | null;
  servedAt: string;
  servedMethod: string;
  servedOn: string;
  signatureDate?: string | null;
  courtName?: string | null;
}

export interface AbsenceDocumentDto {
  fileName: string;
  pdfBase64: string;
}

export interface AbsenceMotionResultDto {
  motion: AbsenceDocumentDto;
  order: AbsenceDocumentDto;
}

/** Thrown when the case is not ready. Carries the reasons so the caller can list them. */
export class AbsenceMotionBlockedError extends Error {
  constructor(
    message: string,
    public readonly blockers: AbsenceBlockerDto[],
  ) {
    super(message);
    this.name = 'AbsenceMotionBlockedError';
  }
}

export default class AbsenceMotionApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  static async readiness(
    ticketGuid: string,
    params: {
      settingDate?: string;
      servedAt?: string;
      servedMethod?: string;
      servedOn?: string;
    },
  ): Promise<AbsenceReadinessDto> {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value) query.set(key, value);
    }
    const queryString = query.toString();
    const response = await fetch(
      `${AbsenceMotionApi.host()}/api/v1/manager/ticket/${ticketGuid}/absence-motion/readiness${
        queryString ? `?${queryString}` : ''
      }`,
      { credentials: 'include' },
    );
    if (!response.ok) {
      throw new Error('Failed to check whether this case is ready for a motion to appear by counsel');
    }
    return response.json();
  }

  static async generate(
    ticketGuid: string,
    input: GenerateAbsenceMotionInput,
  ): Promise<AbsenceMotionResultDto> {
    const response = await fetch(
      `${AbsenceMotionApi.host()}/api/v1/manager/ticket/${ticketGuid}/absence-motion`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      },
    );

    if (response.status === 422) {
      const err = await response.json().catch(() => ({}));
      throw new AbsenceMotionBlockedError(
        err.message || 'This case is not ready for a motion to appear by counsel.',
        Array.isArray(err.blockers) ? err.blockers : [],
      );
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        err.details || err.error || err.message || 'Failed to generate the motion to appear by counsel',
      );
    }
    return response.json();
  }
}
